import React, { useEffect, useState } from 'react';

interface LoadingScreenProps {
  isLoading: boolean;
  onComplete: () => void;
}

export const LoadingScreen: React.FC<LoadingScreenProps> = ({ isLoading, onComplete }) => {
  const [progress, setProgress] = useState(0);
  const [loadingText, setLoadingText] = useState('Initialisation des moteurs...');

  const loadingSteps = [
    { text: 'Initialisation des moteurs...', duration: 800 },
    { text: 'Connexion au réseau Solana...', duration: 1000 },
    { text: 'Chargement des données Jupiter...', duration: 900 },
    { text: 'Synchronisation des Cadets...', duration: 700 },
    { text: 'Préparation de la communauté...', duration: 600 },
    { text: 'Décollage imminent...', duration: 500 }
  ];

  useEffect(() => {
    if (!isLoading) return;

    let currentStep = 0;
    let currentProgress = 0;
    let stepTimeout: NodeJS.Timeout;

    const advanceStep = () => {
      if (currentStep < loadingSteps.length) {
        setLoadingText(loadingSteps[currentStep].text);
        const stepDuration = loadingSteps[currentStep].duration;
        const nextProgress = Math.min(100, Math.round(((currentStep + 1) / loadingSteps.length) * 100));
        setProgress(nextProgress);
        currentStep++;
        stepTimeout = setTimeout(advanceStep, stepDuration);
      } else {
        setProgress(100);
        setLoadingText('Bienvenue à bord !');
        setTimeout(onComplete, 700);
      }
    };

    advanceStep();

    return () => {
      clearTimeout(stepTimeout);
    };
  }, [isLoading]);

  // Injecte les animations additionnelles dans le DOM
  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = additionalAnimations;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gradient-to-br from-emerald-900 via-gray-900 to-cyan-900">
      <style>{`
        .morphing-circle {
          width: 120px;
          height: 120px;
          border-radius: 50%;
          overflow: hidden;
          box-shadow: 0 4px 24px rgba(0,0,0,0.18);
          animation: morph 2.5s infinite cubic-bezier(0.77, 0, 0.175, 1);
        }
        @keyframes morph {
          0%, 100% {
            border-radius: 50% 50% 50% 50% / 50% 50% 50% 50%;
            transform: scale(1) rotate(0deg);
          }
          20% {
            border-radius: 60% 40% 55% 45% / 45% 60% 40% 55%;
            transform: scale(1.04) rotate(8deg);
          }
          40% {
            border-radius: 50% 60% 40% 60% / 60% 40% 60% 40%;
            transform: scale(0.98) rotate(-6deg);
          }
          60% {
            border-radius: 55% 45% 60% 40% / 40% 55% 45% 60%;
            transform: scale(1.03) rotate(4deg);
          }
          80% {
            border-radius: 60% 50% 40% 60% / 60% 50% 40% 60%;
            transform: scale(1) rotate(-4deg);
          }
        }
      `}</style>
      {/* Contenu principal */}
      <div className="relative z-10 text-center max-w-lg mx-4">
        {/* Logo au-dessus du titre */}
        <div className="flex justify-center mb-6">
          <div className="morphing-circle flex items-center justify-center shadow-2xl">
            <img
              src="https://i.imgur.com/FKIlY9U.jpeg"
              alt="Jupipotes Logo"
              className="w-full h-full object-cover"
            />
          </div>
        </div>
        <h1 className="text-4xl lg:text-5xl font-bold text-emerald-100 mb-2 neon-text">
          Jupipotes
        </h1>
        <p className="text-emerald-300/80 text-lg mb-8">
          Exploration spatiale en cours...
        </p>
        {/* Barre de progression avec fusée */}
        <div className="relative mb-6">
          <div className="w-full bg-emerald-900/50 rounded-full h-4 border border-emerald-700/50 overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-emerald-500 to-cyan-400 rounded-full transition-all duration-300 ease-out relative"
              style={{ width: `${progress}%` }}
            >
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer" />
            </div>
          </div>
          <div 
            className="absolute top-1/2 transform -translate-y-1/2 transition-all duration-300 ease-out"
            style={{ 
              left: `calc(${progress}% - 12px)`,
              transform: 'translateY(-50%) rotate(45deg)'
            }}
          >
            <div className="text-2xl animate-rocket">🚀</div>
            {progress > 5 && (
              <div className="absolute -right-8 top-1/2 transform -translate-y-1/2">
                <div className="flex space-x-1">
                  <div className="w-1 h-1 bg-orange-400 rounded-full animate-ping" />
                  <div className="w-1 h-1 bg-yellow-400 rounded-full animate-ping" style={{ animationDelay: '0.2s' }} />
                  <div className="w-1 h-1 bg-red-400 rounded-full animate-ping" style={{ animationDelay: '0.4s' }} />
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="space-y-2">
          <div className="text-2xl font-bold text-cyan-300 neon-text">
            {Math.round(progress)}%
          </div>
          <div className="text-emerald-200 min-h-6 animate-pulse">
            {loadingText}
          </div>
        </div>
        <div className="flex justify-center space-x-2 mt-6">
          {loadingSteps.map((_, index) => (
            <div
              key={index}
              className={`w-2 h-2 rounded-full transition-all duration-300 ${
                (progress / 100) * loadingSteps.length > index
                  ? 'bg-cyan-400 shadow-neon'
                  : 'bg-emerald-800'
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

// CSS animations supplémentaires
const additionalAnimations = `
@keyframes twinkle {
  0%, 100% {
    opacity: 0.3;
    transform: scale(1);
  }
  50% {
    opacity: 1;
    transform: scale(1.2);
  }
}

@keyframes pulse-glow {
  0%, 100% {
    text-shadow: 0 0 20px rgba(16, 185, 129, 0.5);
    transform: scale(1);
  }
  50% {
    text-shadow: 0 0 40px rgba(16, 185, 129, 0.8);
    transform: scale(1.05);
  }
}

@keyframes shimmer {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}

@keyframes rocket {
  0%, 100% {
    transform: translateY(-50%) rotate(-45deg) scale(1);
  }
  50% {
    transform: translateY(-50%) rotate(-45deg) scale(1.1);
  }
}

.animate-twinkle {
  animation: twinkle var(--duration, 3s) ease-in-out infinite;
}

.animate-pulse-glow {
  animation: pulse-glow 2s ease-in-out infinite;
}

.animate-shimmer {
  animation: shimmer 2s ease-in-out infinite;
}

.animate-rocket {
  animation: rocket 1s ease-in-out infinite;
}
`;

export default LoadingScreen;
